---
title: "Usage limits"
description: "Chosen is free — no plans, no caps on jobs or seats. A few actions that cost real money to run are rate-limited; here's which ones and why."
category: "reference"
keywords:
  - "Chosen usage limits"
  - "is Chosen free"
  - "ATS rate limits"
  - "sourcing search limits"
schema: "Article"
nav:
  section: "reference"
  order: 30
---

Chosen is free. There are no plans, no tiers, and no caps on jobs, team members, candidates, or searches. Post as many roles and invite as many people as you like — we make our money when you hire someone through the [marketplace](/guides/marketplace-hiring), not from your subscription.

What does exist is a set of **usage limits** on the handful of actions that cost real money every time they run — an AI model call, a paid enrichment, a web search. These apply to everyone and exist to stop a runaway script or a too-broad query from spending a fortune. They refill continuously, so you don't have to wait out a full day.

## Which actions are metered

- **Deep search** — [AI deep search](/guides/search/deep-search-runs) runs multiple model calls per search. It sits in the strictest tier: a few runs per minute and roughly 100 per day.
- **Enrichment** — paid email lookups for [sourcing](/guides/sourcing/enrichment) sit in a mid-cost tier.
- **Web discovery** — finding new prospects on the open web is an expensive operation, in the same strict tier as deep search.
- **Outreach sending** — sending [outreach](/guides/sourcing/outreach-sending) has its own send budget, plus a separate per-recipient-domain cap (about 5 sends per hour, 50 per day to any one company) that protects your domain reputation.

Everyday work — smart search, boolean search, browsing candidates, moving statuses, posting jobs, inviting teammates — isn't metered. The limits only touch the operations with a real cost behind them, and in normal use you won't notice them.

<Callout type="info" title="If you hit a usage limit, wait a minute and retry">
A usage limit isn't a daily lockout. The buckets refill continuously, so a minute or two later you'll have headroom again. If you're hitting limits constantly on deep search or web discovery, the more likely fix is a narrower query — a broad query burns more budget for worse results.
</Callout>
